import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#111827",
  },
  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: "#4b5563",
  },
  section: {
    marginBottom: 12,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  summaryLabel: {
    fontWeight: 600,
  },
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderStyle: "solid",
    borderRadius: 4,
    overflow: "hidden",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    borderBottomStyle: "solid",
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  cell: {
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: "#e5e7eb",
    borderRightStyle: "solid",
    fontSize: 9,
  },
  lastCell: {
    borderRightWidth: 0,
  },
  headerRow: {
    backgroundColor: "#f3f4f6",
    fontWeight: 600,
  },
  notes: {
    marginTop: 12,
    padding: 10,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderStyle: "solid",
    borderRadius: 4,
    backgroundColor: "#f9fafb",
  },
});

const columns = [
  { key: "index", label: "#", flex: 0.5 },
  { key: "tid", label: "T-ID", flex: 1 },
  { key: "lotId", label: "Lot ID", flex: 1 },
  { key: "styleId", label: "Style", flex: 1 },
  { key: "vendorName", label: "Vendor", flex: 1.2 },
  { key: "color", label: "Color", flex: 1.2 },
  { key: "purchasedQty", label: "Purchased Qty", flex: 1 },
  { key: "receivedQty", label: "Received Qty", flex: 1 },
  { key: "pendingQty", label: "Pending Qty", flex: 1 },
];

const defaultTotals = { purchasedQty: 0, receivedQty: 0, pendingQty: 0 };

const formatNumber = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "0.00";
  }
  return value.toFixed(2);
};

const getCellValue = (row, key) => {
  if (key === "purchasedQty" || key === "receivedQty" || key === "pendingQty") {
    return formatNumber(row[key]);
  }

  return row[key] ?? "-";
};

export default function PurchaseOrderPdf({
  material,
  reportRows = [],
  totals,
  vendorSummary,
  notes,
  dateRange,
}) {
  const resolvedTotals = totals || defaultTotals;
  const vendor = vendorSummary || { name: "-", poNumber: "-", location: "-" };
  // const materialLabel = material
  //   ? `${material?.styleId || "-"} • ${material?.description || ""} • ${
  //       material?.color || ""
  //     }`
  //   : "Material not selected";

  // material label is command separated unique styleIds
  const materialLabel = () => {
    let materialsSet = new Set([]);
    reportRows.forEach((row) => {
      if (row.styleId) {
        if (!materialsSet.has(row.styleId)) {
          materialsSet.add(row.styleId);
        }
      }
    });

    return Array.from(materialsSet).join(", ");
  };

  const dateRangeLabel =
    dateRange?.startDate && dateRange?.endDate
      ? `${dateRange.startDate} → ${dateRange.endDate}`
      : "-";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Purchase Order Report</Text>
          <Text style={styles.subtitle}>Materials: {materialLabel()}</Text>
          <Text style={styles.subtitle}>Date Range: {dateRangeLabel}</Text>
        </View>

        <View style={styles.section}>
          <View style={styles.summaryRow}>
            <Text>
              <Text style={styles.summaryLabel}>Vendor: </Text>
              {vendor.name || "-"}
            </Text>
            <Text>
              <Text style={styles.summaryLabel}>PO: </Text>
              {vendor.poNumber || "-"}
            </Text>
            <Text>
              <Text style={styles.summaryLabel}>Location: </Text>
              {vendor.location || "-"}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>
              <Text style={styles.summaryLabel}>Purchased Qty: </Text>
              {formatNumber(resolvedTotals.purchasedQty)}
            </Text>
            <Text>
              <Text style={styles.summaryLabel}>Received Qty: </Text>
              {formatNumber(resolvedTotals.receivedQty)}
            </Text>
            <Text>
              <Text style={styles.summaryLabel}>Pending Qty: </Text>
              {formatNumber(resolvedTotals.pendingQty)}
            </Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={[styles.tableRow, styles.headerRow]}>
            {columns.map((column, idx) => (
              <Text
                key={column.key}
                style={[
                  styles.cell,
                  { flex: column.flex },
                  idx === columns.length - 1 && styles.lastCell,
                ]}
              >
                {column.label}
              </Text>
            ))}
          </View>
          {reportRows.length === 0 ? (
            <View style={[styles.tableRow, styles.tableRowLast]}>
              <Text style={[styles.cell, styles.lastCell, { flex: 1 }]}>
                No purchase orders selected.
              </Text>
            </View>
          ) : (
            reportRows.map((row, rowIndex) => (
              <View
                key={`${row.id || rowIndex}-${row.tid || rowIndex}`}
                style={[
                  styles.tableRow,
                  rowIndex === reportRows.length - 1 && styles.tableRowLast,
                ]}
              >
                {columns.map((column, colIndex) => (
                  <Text
                    key={`${column.key}-${colIndex}`}
                    style={[
                      styles.cell,
                      { flex: column.flex },
                      colIndex === columns.length - 1 && styles.lastCell,
                    ]}
                  >
                    {getCellValue(row, column.key)}
                  </Text>
                ))}
              </View>
            ))
          )}
        </View>

        {notes && notes.trim() && (
          <View style={styles.notes}>
            <Text style={styles.summaryLabel}>Notes</Text>
            <Text>{notes}</Text>
          </View>
        )}
      </Page>
    </Document>
  );
}
